import React from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useNavigate, useParams } from "react-router-dom";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import StaticTable from "components/Tables/StaticTable";
import Pagination from "components/Pagination";
import { BlueSwitch, PurpleSwitch } from "components/Switch";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import MUITooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { generateAttemptFeedbackUrl, getInterviewAttempts } from "services";
import { getStudentRecentInterviewScoreAndAttempts } from "services/analyticsV2";

import { styled } from "@mui/material/styles";
import { calculatePrevAverage, formatDate, getAverageScore, getPercentageChange } from "utils";
import { Button } from "@mui/material";
import CenterFlexBox from "components/CenterFlexBox";
import ImageProctorDialog from "dialogs/ImageProctorDialog";
import { InterviewTemplateTypes } from "editors/TemplateEditor/constants";
import { SimpleSelect } from "components/CustomSelectFields";
import Tabs from "components/Tabs";
import { WhiteBox } from "components/AppShell";
import FeedbackDialog from "components/FeedbackDialog";
import { IconButton as CustomIconButton } from 'components/CustomButtons';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  PointElement,
  LineElement
);

const useStyles = makeStyles((theme) => ({
  content: {
    height: '600px', padding: '0px', overflow: 'hidden'
  },
  tooltip: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px solid #F0F0F0",
    boxShadow: "5px 3px 13px 0px #4747491A",
    padding: theme.spacing(1, 4),
    transform: "all 200ms",
    pointerEvents: "none",
    width: 300,
  },
  tooltipHeading: {
    ...theme.typography['body01-bold'],
    textTransform: "capitalize",
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  },
  tooltipData: {
    padding: "4px 20px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tooltipDataValue: {
    ...theme.typography['body01-semiBold'],
  },
  tooltipDataLabel: {
    ...theme.typography['body01-medium'],
    color: theme.palette.neutral['clr-800']
  },
  changeChip: {
    width: 60,
    borderRadius: 12,
    padding: theme.spacing(1, 2),
    ...theme.typography['body02-bold'],
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  increment: {
    backgroundColor: theme.palette.success['clr-100'],
    color: theme.palette.success['clr-700'],
  },
  decrement: {
    backgroundColor: theme.palette.danger['clr-100'],
    color: theme.palette.danger['clr-700'],
  },
  performanceLink: {
    color: "#FFFFFF",
    background: "#02569D",
    borderRadius: 12,
    fontWeight: 400,
    fontSize: "10px",
    padding: "4px 8px",
  },
  emptyState: {
    marginBottom: '10px'
  }
}));

const sx = {
  viewBtn: {
    fontSize: '10px',
    padding: '2px 3px',
    color: '#000',
    border: '1px solid #E1E2E3',
    textTransform: 'capitalize',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#02569D',
      color: '#fff'
    }
  }
}

const CustomTooltip = styled(({ className, ...props }) => (
  <MUITooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#FFFFFF",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(12),
    borderRadius: 8,
    boxShadow: "-4px 4px 12px rgba(0, 0, 0, 0.1)",
    padding: 20,
  },
}));

const ShowProctoringResult = ({
  proctoringRecords = [],
  onViewCapturedImage,
}) => {
  let hasViolation = false;
  let moveAwayCount = 0;
  let tabSwitchCount = 0;
  let hasImageProctor = false;
  let cameraDisabled = 0;
  let exitFullScreenCount = 0;

  if (proctoringRecords.length) {
    proctoringRecords.forEach((pr) => {
      if (pr.type === "CAMERA_DISABLED") {
        cameraDisabled++;
      }
      if (pr.type === "IMAGE_PROCTOR") {
        hasImageProctor = true;
      }
      if (pr.type === "GENERAL_PROCTOR") {
        if (pr.moveAwayCount > 0 || pr.tabSwitchCount > 0) {
          hasViolation = true;
          moveAwayCount = pr.moveAwayCount;
          tabSwitchCount = pr.tabSwitchCount;
        }
      }
      if(pr.type === "MOVE_AWAY") {
        hasViolation=true;
        moveAwayCount++;
      }
      if(pr.type === "TAB_SWITCH") {
        hasViolation=true;
        tabSwitchCount++;
      }
      if(pr.type === "EXIT_FULL_SCREEN") {
        hasViolation=true;
        exitFullScreenCount++;
      }
    });
  }

  const handleViewCapturedImage = () => {
    onViewCapturedImage(proctoringRecords);
  };

  return hasViolation ? (
    <CustomTooltip
      placement="right"
      title={
        <React.Fragment>
          <CenterFlexBox justifyContent="flex-start">
            <WarningIcon color="error" /> &nbsp;&nbsp;
            <Typography variant="body1"> Violation Found </Typography>
          </CenterFlexBox>
          <br />

          <ul style={{ padding: 0, margin: 0, marginLeft: 8 }}>
            {moveAwayCount !== 0 && (
              <li style={{ marginBottom: 4 }}>
                {moveAwayCount} times move away from the test screen.
              </li>
            )}
            {tabSwitchCount !== 0 && (
              <li>{tabSwitchCount} times switch between the tabs.</li>
            )}
            {cameraDisabled !== 0 && (
              <li>{cameraDisabled} times disabled the camera.</li>
            )}
            {exitFullScreenCount !== 0 && (
              <li>{exitFullScreenCount} times exited the full screen.</li>
            )}
          </ul>

          {hasImageProctor && (
            <>
              <br />
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleViewCapturedImage}
                style={{ textTransform: "none" }}
              >
                View Captured image
              </Button>
            </>
          )}
        </React.Fragment>
      }
    >
      <WarningIcon color="error" />
    </CustomTooltip>
  ) : (
    <CustomTooltip
      placement="right"
      title={
        <React.Fragment>
          <CenterFlexBox justifyContent="flex-start">
            <VerifiedIcon color="success" /> &nbsp;&nbsp;
            <Typography variant="body1">No Violation Found </Typography>
          </CenterFlexBox>
          <br />
          <Typography variant="body2">
            User was not found with any violations in the assessment as per the
            guidelines.
          </Typography>

          {hasImageProctor && (
            <>
              <br />
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleViewCapturedImage}
                style={{ textTransform: "none" }}
              >
                View Captured image
              </Button>
            </>
          )}
        </React.Fragment>
      }
    >
      <VerifiedIcon color="success" />
    </CustomTooltip>
  );
};

const formatNumberOrDash = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return decimals > 0 ? num.toFixed(decimals) : num.toFixed(0);
};

const commonProperties = {
  renderCell: ({ row, field }) => formatNumberOrDash(row[field], 0),
  align: "center",
};

const AssessmentsColumns = [
  { field: "name", label: "Name", type: "string", minWidth: 160, fixed: 1, disableSorting: true },
  { field: "type", label: "Type", align: "center", minWidth: 100, disableSorting: true },
  { field: "overallScore", label: "Overall Score", type: "number", minWidth: 120, ...commonProperties, disableSorting: true },
  { field: "overallComm", label: "Speech", type: "number", minWidth: 100, ...commonProperties, disableSorting: true },
  { field: "overallContent", label: "Content", type: "number", minWidth: 100, ...commonProperties, disableSorting: true },
  { field: "highestCommScore", label: "Highest Comm.", type: "number", minWidth: 140, ...commonProperties, disableSorting: true },
  { field: "lowestCommScore", label: "Lowest Comm.", type: "number", minWidth: 140, ...commonProperties, disableSorting: true },
  { field: "highestContentScore", label: "Highest Content", type: "number", minWidth: 160, ...commonProperties, disableSorting: true },
  { field: "lowestContentScore", label: "Lowest Content", type: "number", minWidth: 160, ...commonProperties, disableSorting: true },
  { field: "finishedAttempts", label: "Finished Attempts", type: "number", minWidth: 160, ...commonProperties, disableSorting: true },
  { field: "unfinishedAttempts", label: "Unfinished Attempts", type: "number", minWidth: 180, ...commonProperties, disableSorting: true },
  { field: "averageTimeTaken", label: "Avg. Time Taken (min)", minWidth: 180, align: 'center', disableSorting: true, renderCell: ({ row }) => formatNumberOrDash(row.averageTimeTaken, 1) },
  { field: "assessmentAttempted", label: "Assessments Attempted", type: "number", minWidth: 200, ...commonProperties, disableSorting: true },
  { field: "assessmentAvailable", label: "Assessments Available", type: "number", minWidth: 200, ...commonProperties, disableSorting: true },
  { field: "averageQuestionAnswered", label: "Avg. Qs Answered", minWidth: 160, align: 'center', disableSorting: true, renderCell: ({ row }) => formatNumberOrDash(row.averageQuestionAnswered, 1) },
  { field: "mostRecentAttemptAt", label: "Most Recent Attempt", minWidth: 200, align: 'center', disableSorting: true, renderCell: ({ row }) => row.mostRecentAttemptAt ? formatDate(row.mostRecentAttemptAt, 'DD-MM-YYYY HH:mm') : '-' },
];

const ToggleOptions = [
  { _id: "attempt", label: "Attempt" },
  { _id: "assessment", label: "Assessment" },
];

const AssessmentOptions = [
  { _id: "all", name: "All" },
  { _id: "assignment", name: "Assignment" },
  { _id: "practice", name: "Practice" },
];

function AssessmentAnalytics({ student, selectedBatch }) {
  const classes = useStyles();
  const { id } = useParams();
  const navigate = useNavigate();

  const [filter, setFilter] = React.useState("all");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [section, setSection] = React.useState("attempt");
  const [templatesAnalytics, setTemplatesAnalytics] = React.useState([]);
  const [templateAttempts, setTemplateAttempts] = React.useState([]);
  const [forceToggle, setForceToggle] = React.useState();
  const [proctoringRecords, setProctoringRecords] = React.useState([]);
  const [showImageModal, setShowImageModal] = React.useState(false);
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [feedbackUrl, setFeedbackUrl] = React.useState(null);
  const [exportRows, setExportRows] = React.useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage] = React.useState(10);

  const handleViewCapturedImage = (pr) => {
    setProctoringRecords(pr);
    setShowImageModal(true);
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      if (section === "attempt") {
        // For attempt view, fetch all attempts for the selected template
        const { interviewAttempts } = await getInterviewAttempts({
          user: id,
          interviewTemplate: filter || undefined,
          order: "desc",
          orderBy: "startedAt",
          include: ['interviewTemplate'],
          finishedAt: null
        });

        const processedExportRows = interviewAttempts.map((ia, i) => {
          const content = Math.round(ia?.analysis?.content?.ratings?.OVERALL) || 0;
          const comm = Math.round(ia?.analysis.communication.ratings.OVERALL) || 0;

          return {
            _id: ia._id,
            name: `Attempt ${i + 1}`,
            type: "",
            sNo: i + 1,
            date: formatDate(ia.startedAt, "DD MMM YYYY"),
            questionsAttempted: ia.attemptedQuestions.length,
            overallScore: Math.round((content + comm) / 2),
            contentOverall: content,
            communicationOverall: comm,
            proctoringRecords: ia.proctoringRecords || [],
            templateName: ia?.interviewTemplate?.name || '',
            attemptNo: interviewAttempts.length - i
          };
        });

        setExportRows(processedExportRows);
        return processedExportRows;
      } else {
        // For assessment view, fetch all assessments
        const data = await getStudentRecentInterviewScoreAndAttempts({
          studentId: id,
          interviewVariantType: 'assessment',
          batchId: selectedBatch,
          type: 'all',
        });
        const performance = data?.performance || data?.data || [];
        const processedExportRows = performance.map((rowData) => ({
          ...rowData,
          overallScore: getAverageScore(rowData.communicationOverall, rowData.contentOverall),
        }));
        setExportRows(processedExportRows);
        return processedExportRows;
      }
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const AttemptsColumns = React.useMemo(
    () => [
      {
        field: "sNo",
        label: "S.No.",
        type: "number",
        minWidth: 50,
        align: "center",
        fixed: 1,
        disableSorting: true,
        disableExport: true,
        renderCell: ({ row, field, index }) =>
          index + 1
      },
      {
        field: "proctor",
        label: "Proctor",
        disableExport: true,
        align: "center",
        disableSorting: true,
        disableExport: true,
        renderCell: ({ row }) => (
          <ShowProctoringResult
            proctoringRecords={row.proctoringRecords}
            onViewCapturedImage={handleViewCapturedImage}
          />
        ),
      },
      {
        field: "attemptNo",
        label: "Attempt No.",
        align: "center",
        disableSorting: true,
      },
      {
        field: "templateName",
        label: "Assessment",
        align: "center",
        disableSorting: true,
      },
      {
        field: "date", label: "Date", type: 'date', minWidth: 60,
        disableSorting: true,
        renderCell: ({ row, field }) => {
          return formatDate(row[field], "DD-MM-YYYY")
        }
      },
      {
        field: "questionsAttempted",
        label: "Questions attempted",
        minWidth: 150,
        disableSorting: true,
        ...commonProperties
      },
      {
        field: "overallScore",
        label: "Overall Score",
        type: "number",
        minWidth: 130,
        disableSorting: true,
        ...commonProperties
      },
      {
        field: "communicationOverall",
        label: "Speech",
        type: "number",
        minWidth: 80,
        disableSorting: true,
        ...commonProperties
      },
      {
        field: "contentOverall",
        label: "Content",
        type: "number",
        minWidth: 80,
        disableSorting: true,
          ...commonProperties
      },
    ],
    []
  );
  const [legendStates, setLegendStates] = React.useState({
    speech: true,
    content: true,
  });

  const assessmentInitialFilters = [
    "name", "type", "domain", "publishOn", "overallScore", "contentOverall",
    "communicationOverall", "finishedAttempts", "unfinishedAttempts"
  ]
  const attemptInitialFilters = [
    "sNo", "proctor", "domain", "date", "questionsAttempted", "overallScore",
    "communicationOverall", "contentOverall", "templateName", "attemptNo"
  ]

  React.useEffect(() => {
    if (section !== 'assessment') return;
    setLoading(true);
    getStudentRecentInterviewScoreAndAttempts({
      studentId: id,
      interviewVariantType: 'assessment',
      batchId: selectedBatch,
      page: 1,
      pageSize: 10,
    }).then((res) => {
      const perf = res?.performance || res?.data || [];
      setRows(perf);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id, selectedBatch, section]);

  React.useEffect(() => {
    if (section === "attempt") {
      if (templateAttempts[filter]) {
        setRows(templateAttempts[filter]);
      } else {
        getInterviewAttempts({
          user: id,
          interviewTemplate: filter || undefined,
          order: "desc",
          orderBy: "startedAt",
          include: ['interviewTemplate'],
          finishedAt: null
        })
          .then(({ interviewAttempts }) => {
            setTemplateAttempts((ta) => {
              const _templateAttempts = { ...ta };

              _templateAttempts[filter] = interviewAttempts.map((ia, i) => {
                const content = Math.round(ia?.analysis?.content?.ratings?.OVERALL) || 0;
                const comm = Math.round(ia?.analysis.communication.ratings.OVERALL) || 0;

                return {
                  _id: ia._id,
                  name: `Attempt ${i + 1}`,
                  type: "",
                  sNo: i + 1,
                  date: formatDate(ia.startedAt, "DD MMM YYYY"),
                  questionsAttempted: ia.attemptedQuestions.length,
                  overallScore: Math.round((content + comm) / 2),
                  contentOverall: content,
                  communicationOverall: comm,
                  disabledClick: true,
                  proctoringRecords: ia.proctoringRecords || [],
                  templateName: ia?.interviewTemplate?.name || '',
                  attemptNo: interviewAttempts.length - i
                };
              });

              setRows(_templateAttempts[filter]);

              return _templateAttempts;
            });
          })
          .catch(console.error);
      }
    } else {
      if (filter === "practice" || filter === "assignment") {
        setRows(
          templatesAnalytics.filter((row) => row.type === filter).slice(0, 10)
        );
      } else {
        setRows(templatesAnalytics);
      }
    }
  }, [filter]);

  React.useEffect(() => {
    if (section === "assessment") setFilter("all");
    else {
      if (forceToggle === undefined) {
        setFilter(templatesAnalytics?.[0]?._id || "");
      } else {
        setForceToggle(undefined);
      }
    }
  }, [section]);

  const [datasets, labels] = React.useMemo(() => {
    let _labels = [];
    let dataToConsider = [];
    const commData = [];
    const contentData = [];

    if (section === "assessment") {
      dataToConsider = rows.slice(0, 10);

      for (const data of dataToConsider) {
        if (legendStates.speech) commData.push(Number(data.overallComm) || 0);
        if (legendStates.content) contentData.push(Number(data.overallContent) || 0);

        let label = (data.name || '').substr(0, 8);
        if ((data.name || '').length > 8) label = label.concat("...");
        _labels.push(label || '');
      }
    } else {
      dataToConsider = rows.slice(0, 10);

      let no = 1;
      for (const data of dataToConsider) {
        if (legendStates.speech) commData.push(data.communicationOverall || 0);
        if (legendStates.content) contentData.push(data.contentOverall || 0);
        _labels.push(no++);
      }
    }

    if (_labels.length < 10) {
      for (let x = _labels.length; x < 10; x++) _labels.push("");
    }

    const _datasets = [];

    if (legendStates.speech) {
      _datasets.push({
        label: "Communication",
        data: commData,
        backgroundColor: "#B8ACF6",
        hoverBackgroundColor: '#8270DB',
      });
    }

    if (legendStates.content) {
      _datasets.push({
        label: "Content",
        data: contentData,
        backgroundColor: "#9DE4F2",
        hoverBackgroundColor: '#52B8CC'
      });
    }

    return [_datasets, _labels];
  }, [rows, section, legendStates]);

  const getTooltip = React.useCallback(
    (context) => {
      const { chart } = context;
      // Tooltip Element
      let tooltipEl = document.getElementById("chartjs-tooltip");

      // Create element on first render
      if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = "chartjs-tooltip";
        tooltipEl.classList.add(classes.tooltip);
        chart.canvas.style.position = "relative";
        chart.canvas.parentNode.appendChild(tooltipEl);
      }
      tooltipEl.innerHTML = "";

      // Hide if no tooltip
      const tooltipModel = context.tooltip;
      if (tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
      }

      // Set caret Position
      tooltipEl.classList.remove("above", "below", "no-transform");
      if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
      } else {
        tooltipEl.classList.add("no-transform");
      }

      let dataToConsider = rows.slice(0, 10);

      const dataIndex = tooltipModel.dataPoints?.[0].dataIndex;
      const data = dataToConsider[dataIndex];
      // Previous delta metrics not applicable with new simplified graph

      const heading = document.createElement("p");
      heading.className = classes.tooltipHeading;
      heading.innerHTML =
        section === "assessment"
          ? `${data?.name} (${data?.type?.toUpperCase()})`
          : `${data?.name}`;

      tooltipEl.appendChild(heading);

      const dataContainer = document.createElement("div");
      dataContainer.style.width = "100%";

      let innerHTML = `
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Speech</div>
        <div class='${classes.tooltipDataValue}'>${data.overallComm || 0}</div>
      </div>
      <div class='${classes.tooltipData}'>
        <div class='${classes.tooltipDataLabel}'>Content</div>
        <div class='${classes.tooltipDataValue}'>${data.overallContent || 0}</div>
      </div>
    `;

      dataContainer.innerHTML = innerHTML;

      tooltipEl.appendChild(dataContainer);

      const { offsetLeft: positionX, offsetTop: positionY } =
        context.chart.canvas;

      // Display, position, and set styles for font
      tooltipEl.style.opacity = 1;
      tooltipEl.style.zIndex = 10000000000;
      tooltipEl.style.left = positionX + tooltipModel.caretX - 250 + "px";
      tooltipEl.style.top = positionY + tooltipModel.caretY - 140 + "px";
    },
    [rows, classes, section]
  );

  const GraphOptions = {
    responsive: true,
    aspectRatio: 5,
    maintainAspectRatio: true,
    interaction: { mode: "index", intersect: false },
    // onClick: handleClick,
    plugins: {
      legend: { display: false },
      title: { display: false, text: "Performance" },

      tooltip: { enabled: false, position: "nearest", external: section === "assessment" && getTooltip },
    },
    scales: { yAxis: { min: 0, max: 100 } },
  };

  const handleToggleChange = (option) => {
    setSection(option);
  };

  const handleFilterChange = (e) => setFilter(e.target.value);

  const handleLegendChange = (event) => {
    setLegendStates((ls) => ({
      ...ls,
      [event.target.name]: event.target.checked,
    }));
  };

  const _rows = React.useMemo(() => {
    const allRows = rows?.map((rowData) => {
      return {
        ...rowData,
        overallScore: getAverageScore(rowData.communicationOverall, rowData.contentOverall),
        disable: !rowData.attemptedBy?.includes(id)
      }
    })

    return allRows
  }, [rows, section]);

  // Pagination logic
  const totalItems = _rows?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRows = _rows?.slice(startIndex, endIndex) || [];

  // Reset to first page when section changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [section, filter]);

  const handelViewFeedback = async (id) => {
    const url = await generateAttemptFeedbackUrl(id);
    setFeedbackUrl(url);
  }

  const getActions = [
    {
      title: "View Performance",
      renderIcon: () => <Button sx={sx.viewBtn}>
        <RemoveRedEyeIcon style={{ padding: '1px', marginRight: '5px', fontSize: '20px' }} />
        View
      </Button>,
      label: "View Performance",
      onClick: (param) => handelViewFeedback(param.id)
    }
  ];

  const getAssessmentActions = [
    {
       title: "analytics",
       label: "Feedback", 
       minWidth: 120, 
       disableExport: true,
       disableSorting: true,
       align: "center", 
       renderIcon: () =>
        <CustomIconButton  variant='contained'
          style={{
                    border : `1px solid #003E8C`,
                    padding : '4px',
                    // marginLeft : '72px'
                }}
        >
          <img 
            src='https://assets.languify.in/images/PerformanceIcon.svg' 
            alt='performance' 
          />
     </CustomIconButton>,
       onClick: (param) => navigate(`/assessments/${param.id}/performance`)
 }
  ];

  return (
    <>
      <WhiteBox sx={{ m: 0, width: '100%' }}>
        <Box display='flex' flexDirection='column' gap='20px'>
          <Box display="flex" gap={2}>
            <Box display='flex' flexDirection='column' gap={2}>
              <Typography variant="body01-bold">
                Sort By
              </Typography>
              <Tabs
                onChange={handleToggleChange}
                options={ToggleOptions}
                value={section}
              />
            </Box>
            {
              section !== "assessment" &&
              <SimpleSelect
                label="Select Assessment"
                options={templatesAnalytics}
                value={filter}
                onSelect={handleFilterChange}
                sx={{ maxWidth: '300px' }}
              />
            }
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display='flex' flexDirection='column' gap='4px' width='80%'>
              <Typography variant='h4-medium'>
                Users Performance
              </Typography>
              <Typography variant="h6-medium" color='neutral.clr-600'>
                {
                  section === "assessment"
                    ? "The graph tooltip shows changes in values for each assessment, comparing them with the cumulative average, considering all prior practice assessments and assignments"
                    : "Select any assessment from the drop-down menu on the right to compare your performance attempt-wise against the average of your previous attempts."
                }
              </Typography>
            </Box>
            {section === "assessment" ? (
              <SimpleSelect
                label="Assessment Type"
                options={AssessmentOptions}
                value={filter}
                onSelect={handleFilterChange}
              />
            ) : null}
          </Box>
          <Box
            sx={{
              width: "100%",
              background: "#FFFFFF",
              border: "1px solid lightgrey",
              borderRadius: 2,
              padding: 4,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              marginTop: '8px'
            }}
          >
            <Box textAlign="right">
              <FormControlLabel
                control={
                  <BlueSwitch
                    name="speech"
                    checked={legendStates.speech}
                    onChange={handleLegendChange}
                    sx={{ "& .track": { background: "#007AFF" } }}
                  />
                }
                label="Speech"
              />{" "}
              &nbsp;&nbsp;
              <FormControlLabel
                control={
                  <PurpleSwitch
                    name="content"
                    checked={legendStates.content}
                    onChange={handleLegendChange}
                  />
                }
                label="Content"
              />
            </Box>
            <Box display="flex" alignItems="center">
              <Typography
                fontSize={15}
                color={"#666666"}
                component="div"
                style={{ transform: "rotate(-90deg)" }}
              >
                Score
              </Typography>
              <div style={{ flexGrow: 1 }}>
                <Bar options={GraphOptions} data={{ labels, datasets }} />
              </div>
            </Box>
            <div>
              <Typography fontSize={15} mt={1} color={"#666666"}>
                {section === "assessment" ? "Assessments" : "Attempts"}
              </Typography>
            </div>
          </Box>
          <br />

          <StaticTable
            columns={section === "assessment" ? AssessmentsColumns : AttemptsColumns}
            loading={loading}
            rows={paginatedRows}
            exportRows={exportRows}
            actions={section === "assessment" ? getAssessmentActions : getActions}
            initialFilters={section === "assessment" ? assessmentInitialFilters : attemptInitialFilters}
            noRowsMessage="No assessments taken by the user!!"
            sortByColumns={
              section === 'assessment' ?
                [
                  { ...AssessmentsColumns[4], sortByTitle: "Scores: High to Low", sortBy: 'desc' },
                  { ...AssessmentsColumns[4], sortByTitle: "Scores: Low to High", sortBy: 'asc' },
                  { ...AssessmentsColumns[9], sortByTitle: "Engagement: High to Low", sortBy: 'desc' },
                  { ...AssessmentsColumns[9], sortByTitle: "Engagement: Low to High", sortBy: 'asc' }
                ]
                :
                [
                  { ...AttemptsColumns[4], sortByTitle: "Scores: High to Low", sortBy: 'desc' },
                  { ...AttemptsColumns[4], sortByTitle: "Scores: Low to High", sortBy: 'asc' },
                ]
            }
            rowSelectionModel={rowSelectionModel}
            setRowSelectionModel={setRowSelectionModel}
            useItemProps={{
              name: student?.name,
              exportService: null,
              payload: null,
              onExport: handleExport
            }}
            userId={id}
            selectedBatch={selectedBatch}
          />
          
          {/* Pagination Component */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
              <Typography variant="h6-medium" color="neutral.clr-500" display="flex" gap="4px">
                Showing 
                <Typography variant="h6-medium" color="neutral.clr-900">
                  {Math.min(startIndex + 1, totalItems)}-{Math.min(endIndex, totalItems)}
                </Typography> 
                of 
                <Typography variant="h6-medium" color="neutral.clr-900">
                  {totalItems}
                </Typography> 
                entries
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                setPage={setCurrentPage}
              />
            </Box>
          )}
        </Box>
      </WhiteBox>
      <ImageProctorDialog
        open={showImageModal}
        proctoringRecords={proctoringRecords}
        onClose={() => setShowImageModal(false)}
      />
      <FeedbackDialog
        openDialog={feedbackUrl ? true : false}
        setOpenDialog={() => setFeedbackUrl(null)}
        url={feedbackUrl}
      />
    </>
  );
}

export default AssessmentAnalytics;
